import React, { useEffect, useState, type Key } from 'react';
import { Row, Col, Button, message, Popover, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Entity } from 'store/ducks/InternalControls/types';
import { RootState } from 'store/configureStore';
import { internalControlsOp } from 'store/ducks/InternalControls';
import { AllocationConfig } from 'store/ducks/Global/types';
import Helpers from 'utils/Helpers';
import WhoAmILayout from 'components/Layouts/WhoAmILayout';
import CustomSelect from 'components/CustomSelect';
import ApiService from 'utils/ApiCalls';
import * as Styled from '../WhoAmI.styled';

const WhoAmIAllocation: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { scheme, lang } = useSelector((state: RootState) => ({
    scheme: state.global.scheme,
    lang: state.global.lang
  }));

  const [targets, setTargets] = useState<Record<string, Key[]>>({});
  const [initialTargets, setInitialTargets] = useState<Record<string, Key[]>>(
    {}
  );
  const [savePopup, setSavePopup] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [selectedParents, setSelectedParents] = useState<
    Record<string, number>
  >({});
  const [industryOptions, setIndustryOptions] = useState<
    { id: number; name: string }[]
  >([]);

  const [groups, setGroups] = useState<Entity[]>([]);
  const [divisions, setDivisions] = useState<Entity[]>([]);
  const [regions, setRegions] = useState<Entity[]>([]);
  const [legalEntities, setLegalEntities] = useState<Entity[]>([]);
  const [businessUnits, setBusinessUnits] = useState<Entity[]>([]);
  const [modules, setModules] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const filters = await ApiService.getFilters(lang);

        const data = filters?.data;

        setGroups(data.group || []);
        setDivisions(data.division || []);
        setRegions(data.region || []);
        setLegalEntities(data.legal_entity || []);
        setBusinessUnits(data.business_unit || []);
        setIndustryOptions(
          (data.industry || []).map((industry: any) => ({
            ...industry,
            name: industry.value
          }))
        );
        setModules(
          (data.module || []).map((item: any) => ({
            id: item.id,
            name: item.value
          }))
        );
      } catch {
        message.error(t('WHO_AM_I_SAVE_ERROR'));
      }
    };

    load();
  }, [lang]);

  const loadAllocatedIndustries = async (type: string, parentId: number) => {
    try {
      const payload =
        type === 'group'
          ? { group_id: parentId }
          : type === 'division'
          ? { division_id: parentId }
          : type === 'region'
          ? { region_id: parentId }
          : type === 'legal_entity'
          ? { legal_entity_id: parentId }
          : { business_unit_id: parentId };

      const res = await ApiService.listIndustriesByHierarchy(payload);

      const industries = res?.data?.data || [];

      const industryIds = industries.map((i: any) => String(i.ind_id));

      setTargets((prev) => ({
        ...prev,
        [type]: industryIds
      }));

      setInitialTargets((prev) => ({
        ...prev,
        [type]: industryIds
      }));
    } catch {
      message.error(t('WHO_AM_I_SAVE_ERROR'));
    }
  };

  const loadIndustriesForModule = async (moduleId: number) => {
    try {
      const results = await Promise.all(
        industryOptions.map(async (industry) => {
          const response = await ApiService.getModulesByIndustries({
            lang,
            ind_id: industry.id
          });

          const mappedModules = response?.data?.data || [];

          const isMapped = mappedModules.some(
            (item: any) => Number(item.id ?? item.mod_id) === moduleId
          );

          return isMapped ? String(industry.id) : null;
        })
      );

      const mappedIndustryIds = results.filter(
        (id): id is string => id !== null
      );

      setTargets((prev) => ({
        ...prev,
        module: mappedIndustryIds
      }));

      setInitialTargets((prev) => ({
        ...prev,
        module: mappedIndustryIds
      }));
    } catch {
      message.error(t('WHO_AM_I_SAVE_ERROR'));
    }
  };

  const configs: AllocationConfig[] = [
    {
      id: 'group',
      title: t('ALLOCATE_INDUSTRY_TO_GROUP'),
      options: groups
    },
    {
      id: 'division',
      title: t('ALLOCATE_INDUSTRY_TO_DIVISION'),
      options: divisions
    },
    {
      id: 'region',
      title: t('ALLOCATE_INDUSTRY_TO_REGION'),
      options: regions
    },
    {
      id: 'legal_entity',
      title: t('ALLOCATE_INDUSTRY_TO_LEGAL_ENTITY'),
      options: legalEntities
    },
    {
      id: 'business_unit',
      title: t('ALLOCATE_INDUSTRY_TO_BUSINESS_UNIT'),
      options: businessUnits
    },
    {
      id: 'module',
      title: t('INDUSTRY_TO_MODULE'),
      options: modules
    }
  ];

  const isParentMissing = (id: string) => !selectedParents[id];
  const hasChanges = (id: string) =>
    !Helpers.areKeysEqual(targets[id] || [], initialTargets[id] || []);
  const isBlocked = (id: string) => isParentMissing(id) || !hasChanges(id);

  useEffect(() => {
    configs.forEach((c) => {
      if (!isBlocked(c.id)) {
        setSavePopup((prev) => ({
          ...prev,
          [c.id]: false
        }));
      }
    });
  }, [targets, selectedParents, initialTargets]);

  const handleSave = async (type: string) => {
    try {
      setLoading((p) => ({ ...p, [type]: true }));

      const selectedEntityId = selectedParents[type];

      if (!selectedEntityId) {
        message.error(t('WHO_AM_I_VALIDATION_ERROR'));
        return;
      }

      const industryIds = (targets[type] || []).map(Number);

      const payload = {
        [`${type}_id`]: selectedEntityId,
        industry_ids: industryIds
      };

      switch (type) {
        case 'group':
          await ApiService.allocateIndustryTypeToGroups(payload);
          break;

        case 'division':
          await ApiService.allocateIndustryTypeToDivisions(payload);
          break;

        case 'region':
          await ApiService.allocateIndustryTypeToRegions(payload);
          break;

        case 'legal_entity':
          await ApiService.allocateIndustryTypeToLegalEntities(payload);
          break;

        case 'business_unit':
          await ApiService.allocateIndustryTypeToBusinessUnits(payload);
          break;

        case 'module':
          await ApiService.mapIndustriesToModule({
            mod_id: selectedEntityId,
            industry_ids: industryIds
          });
          break;
      }

      setInitialTargets((p) => ({
        ...p,
        [type]: targets[type]
      }));

      message.success(t('WHO_AM_I_SAVE_SUCCESS'));
    } catch (e: any) {
      message.error(e?.message || t('WHO_AM_I_SAVE_ERROR'));
    } finally {
      setLoading((p) => ({ ...p, [type]: false }));
    }
  };

  useEffect(() => {
    const hasAnyChanges = configs.some((c) => {
      return (
        selectedParents[c.id] &&
        !Helpers.areKeysEqual(targets[c.id], initialTargets[c.id])
      );
    });
    dispatch(internalControlsOp.setHasChanges(hasAnyChanges));
  }, [targets, initialTargets, selectedParents, dispatch]);

  return (
    <WhoAmILayout>
      <Styled.ContentWrapper>
        <Row gutter={[24, 24]}>
          {configs.map((c) => {
            return (
              <Col xs={24} sm={24} md={12} lg={12} xl={12} key={c.id}>
                <Styled.TransferCard
                  scheme={scheme}
                  title={c.title}
                  extra={
                    <Popover
                      content={
                        !selectedParents[c.id]
                          ? t('WHO_AM_I_VALIDATION_ERROR')
                          : t('UNSAVED_CHANGES_TITLE')
                      }
                      open={savePopup[c.id]}
                    >
                      <Button
                        type='primary'
                        loading={loading[c.id]}
                        disabled={isBlocked(c.id)}
                        onClick={() => {
                          const parentMissing = isParentMissing(c.id);
                          const noTransfer = !hasChanges(c.id);

                          if (parentMissing || noTransfer) {
                            setSavePopup((prev) => ({
                              ...prev,
                              [c.id]: true
                            }));

                            setTimeout(() => {
                              setSavePopup((prev) => ({
                                ...prev,
                                [c.id]: false
                              }));
                            }, 1500);

                            return;
                          }

                          handleSave(c.id);
                        }}
                      >
                        {t('SAVE')}
                      </Button>
                    </Popover>
                  }
                >
                  <CustomSelect
                    placeholder={`${t('SELECT')} ${c.title}`}
                    value={selectedParents[c.id] || null}
                    onChange={async (value: number) => {
                      setSelectedParents((prev) => ({
                        ...prev,
                        [c.id]: value
                      }));

                      setTargets((prev) => ({ ...prev, [c.id]: [] }));
                      setInitialTargets((prev) => ({ ...prev, [c.id]: [] }));

                      await loadAllocatedIndustries(c.id, value);
                    }}
                    options={c.options.map((item) => ({
                      label: item.name,
                      value: item.id
                    }))}
                  />

                  <Styled.EqualTransfer
                    className='equal-transfer'
                    dataSource={industryOptions.map((industry) => ({
                      key: String(industry.id),
                      title: industry.name
                    }))}
                    targetKeys={targets[c.id] || []}
                    onChange={(keys) =>
                      setTargets((p) => ({ ...p, [c.id]: keys }))
                    }
                    render={(item) => <div>{String(item.title || '')}</div>}
                    showSearch
                    disabled={!selectedParents[c.id]}
                    listStyle={{ height: 280 }}
                  />
                </Styled.TransferCard>
              </Col>
            );
          })}
        </Row>
      </Styled.ContentWrapper>
    </WhoAmILayout>
  );
};

export default WhoAmIAllocation;
